import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  Timestamp,
  writeBatch,
  getDoc,
  orderBy,
  limit,
  collectionGroup,
} from "firebase/firestore";
import { db } from "../config/firebase";
import type {
  DrinkWithVerifications,
  Verification,
  VerificationStats,
  UserContributionStats,
} from "../types/verification.types";

function buildVerificationCounts(verifications: Verification[]) {
  return {
    confirmed: verifications.filter((v) => v.result === "confirmed").length,
    not_found: verifications.filter((v) => v.result === "not_found").length,
    wrong_price: verifications.filter((v) => v.result === "wrong_price").length,
    total: verifications.length,
  };
}

function createBarNameLoader() {
  const cache = new Map<string, Promise<string>>();

  return (barId: string): Promise<string> => {
    if (!barId) {
      return Promise.resolve("Unknown Bar");
    }

    if (!cache.has(barId)) {
      cache.set(
        barId,
        getDoc(doc(db, "bars", barId))
          .then((barDoc) => {
            if (!barDoc.exists()) {
              return barId;
            }
            return barDoc.data().name || barId;
          })
          .catch(() => barId)
      );
    }

    return cache.get(barId)!;
  };
}

/**
 * Lädt alle Drinks mit User-Contributions (isUserContributed = true)
 * die noch nicht approved sind
 */
export const getPendingUserContributions = async (): Promise<
  DrinkWithVerifications[]
> => {
  try {
    const loadBarName = createBarNameLoader();

    // CollectionGroup Query über alle drinks in allen bars
    const drinksQuery = query(
      collectionGroup(db, "drinks"),
      where("isUserContributed", "==", true),
      where("approved", "==", false),
      orderBy("contributedAt", "desc"),
      limit(100)
    );

    const snapshot = await getDocs(drinksQuery);
    const drinks = await Promise.all(
      snapshot.docs.map(async (drinkDoc) => {
        const drinkData = drinkDoc.data();
        const barId = drinkDoc.ref.parent.parent?.id || "";

        const [barName, verificationsSnapshot] = await Promise.all([
          loadBarName(barId),
          getDocs(collection(drinkDoc.ref, "verifications")),
        ]);

        const verifications: Verification[] = verificationsSnapshot.docs.map(
          (v) => v.data() as Verification
        );

        return {
          id: drinkDoc.id,
          barId,
          barName,
          name: drinkData.name,
          category: drinkData.category,
          price: drinkData.price,
          volume: drinkData.volume,
          description: drinkData.description,
          imageUrl: drinkData.imageUrl,
          approved: drinkData.approved,
          isUserContributed: drinkData.isUserContributed,
          contributedBy: drinkData.contributedBy,
          contributedAt: drinkData.contributedAt,
          verifications,
          verificationsCount: buildVerificationCounts(verifications),
        } satisfies DrinkWithVerifications;
      })
    );

    return drinks;
  } catch (error) {
    console.error("Error fetching pending user contributions:", error);
    throw error;
  }
};

/**
 * Genehmigt einen User-Contribution Drink
 * Setzt approved = true und gibt dem User Punkte
 */
export const approveUserContribution = async (
  barId: string,
  drinkId: string,
  adminUid: string
): Promise<void> => {
  try {
    const drinkRef = doc(db, "bars", barId, "drinks", drinkId);
    const drinkSnap = await getDoc(drinkRef);

    if (!drinkSnap.exists()) {
      throw new Error("Drink not found");
    }

    const drinkData = drinkSnap.data();
    const contributedBy = drinkData.contributedBy;

    const batch = writeBatch(db);

    // 1. Approve Drink
    batch.update(drinkRef, {
      approved: true,
      approvedAt: Timestamp.now(),
      approvedBy: adminUid,
    });

    // 2. Update User: +10 Punkte für approved drink
    if (contributedBy) {
      const userRef = doc(db, "users", contributedBy);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        const currentScore = userData.contributionScore || 0;
        batch.update(userRef, {
          contributionScore: currentScore + 10,
        });

        // Optional: Badge "first_contribution" vergeben
        if (!userData.badges?.includes("first_contribution")) {
          batch.update(userRef, {
            badges: [...(userData.badges || []), "first_contribution"],
          });
        }
      }
    }

    await batch.commit();
  } catch (error) {
    console.error("Error approving user contribution:", error);
    throw error;
  }
};

/**
 * Lehnt einen User-Contribution Drink ab
 * Setzt approved = false und archiviert
 */
export const rejectUserContribution = async (
  barId: string,
  drinkId: string,
  adminUid: string,
  reason?: string
): Promise<void> => {
  try {
    const drinkRef = doc(db, "bars", barId, "drinks", drinkId);

    await updateDoc(drinkRef, {
      approved: false,
      rejectedAt: Timestamp.now(),
      rejectedBy: adminUid,
      rejectionReason: reason || "Rejected by admin",
      archived: true,
    });
  } catch (error) {
    console.error("Error rejecting user contribution:", error);
    throw error;
  }
};

/**
 * Lädt Verifications Stats für Dashboard
 */
export const getVerificationStats = async (): Promise<VerificationStats> => {
  try {
    const pendingQuery = query(
      collectionGroup(db, "drinks"),
      where("isUserContributed", "==", true),
      where("approved", "==", false)
    );
    const pendingSnapshot = await getDocs(pendingQuery);

    let needsReview = 0;
    let totalVerifications = 0;

    for (const drinkDoc of pendingSnapshot.docs) {
      const verificationsRef = collection(drinkDoc.ref, "verifications");
      const verificationsSnapshot = await getDocs(verificationsRef);
      totalVerifications += verificationsSnapshot.size;
      if (verificationsSnapshot.size >= 3) {
        needsReview++;
      }
    }

    return {
      totalPendingDrinks: pendingSnapshot.size,
      totalVerifications,
      needsReview,
      autoApproved: 0,
      autoRejected: 0,
    };
  } catch (error) {
    console.error("Error fetching verification stats:", error);
    return {
      totalPendingDrinks: 0,
      totalVerifications: 0,
      needsReview: 0,
      autoApproved: 0,
      autoRejected: 0,
    };
  }
};

/**
 * Berechnet wie viele Verifications eines Users korrekt waren.
 * Korrekt = "confirmed" bei später approved Drink  |  "not_found" bei archived Drink
 */
const calculateVerificationAccuracy = async (
  userId: string
): Promise<{ total: number; correct: number }> => {
  const verQuery = query(
    collectionGroup(db, "verifications"),
    where("userID", "==", userId),
    limit(50)
  );
  const verSnapshot = await getDocs(verQuery);

  if (verSnapshot.empty) return { total: 0, correct: 0 };

  let correct = 0;
  const drinkCache = new Map<string, Promise<Record<string, unknown> | null>>();

  await Promise.all(
    verSnapshot.docs.map(async (verDoc) => {
      const drinkRef = verDoc.ref.parent.parent;
      if (!drinkRef) return;

      const cacheKey = drinkRef.path;
      if (!drinkCache.has(cacheKey)) {
        drinkCache.set(
          cacheKey,
          getDoc(drinkRef).then((drinkSnap) =>
            drinkSnap.exists() ? (drinkSnap.data() as Record<string, unknown>) : null
          )
        );
      }

      const drink = await drinkCache.get(cacheKey)!;
      if (!drink) return;

      const result = verDoc.data().result as string;
      if (result === "confirmed" && drink.approved === true) correct++;
      if (result === "not_found" && drink.archived === true) correct++;
    })
  );

  return { total: verSnapshot.size, correct };
};

/**
 * Lädt Top Contributors (User mit höchstem Contribution Score)
 */
export const getTopContributors = async (
  limitCount: number = 10
): Promise<UserContributionStats[]> => {
  try {
    const usersQuery = query(
      collection(db, "users"),
      orderBy("contributionScore", "desc"),
      limit(limitCount)
    );

    const snapshot = await getDocs(usersQuery);
    return Promise.all(
      snapshot.docs.map(async (userDoc) => {
        const userData = userDoc.data();

        const drinksQuery = query(
          collectionGroup(db, "drinks"),
          where("contributedBy", "==", userDoc.id),
          where("isUserContributed", "==", true)
        );

        const [drinksSnapshot, verificationAccuracy] = await Promise.all([
          getDocs(drinksQuery),
          calculateVerificationAccuracy(userDoc.id),
        ]);

        const approvedDrinks = drinksSnapshot.docs.filter(
          (d) => d.data().approved === true
        ).length;
        const pendingDrinks = drinksSnapshot.docs.filter(
          (d) => d.data().approved === false && !d.data().archived
        ).length;

        const totalVerifications = verificationAccuracy.total;
        const correctVerifications = verificationAccuracy.correct;
        const trustScore =
          totalVerifications > 0
            ? Math.round((correctVerifications / totalVerifications) * 100)
            : null;

        return {
          userId: userDoc.id,
          userName: userData.name || userData.displayName || "Unbekannter User",
          email: userData.email || "",
          contributionScore: userData.contributionScore || 0,
          totalDrinks: drinksSnapshot.size,
          approvedDrinks,
          pendingDrinks,
          totalVerifications,
          correctVerifications,
          trustScore,
          badges: userData.badges || [],
        } satisfies UserContributionStats;
      })
    );
  } catch (error) {
    console.error("Error fetching top contributors:", error);
    return [];
  }
};
